import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';
import { CustelOTPInput } from '../../components/ui/CustelOTPInput';

/**
 * E-posta doğrulama (login → EMAIL_VERIFICATION_REQUIRED yönlendirmesi).
 *
 * Style A (auth-card) — LoginPage / EmailOtpPage / ForgotPasswordPage ile aynı düzen.
 * Kod girişi segment'li `CustelOTPInput` (tüm OTP ekranlarında tutarlı).
 * E-posta `location.state`'ten gelir → read-only; yoksa fallback düzenlenebilir alan.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();

  const navState = location.state as { email?: string; codeAlreadySent?: boolean } | null;
  const stateEmail = navState?.email ?? '';
  // Login / telefon-doğrulama zinciri buraya yönlendirmeden ÖNCE zaten bir kod yolladıysa
  // `codeAlreadySent=true` gelir → mount'ta ikinci kez göndermeyiz (çift kod bug'ı önlenir).
  const codeAlreadySent = navState?.codeAlreadySent === true;
  const [email, setEmail] = useState(stateEmail);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // Resend cooldown sayaç.
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  // Mount'ta kodu otomatik gönder (enumeration-safe; backend daima 204). StrictMode guard.
  // `codeAlreadySent` ise gönderim ATLANIR (login/telefon zinciri zaten yolladı) — yalnız
  // cooldown başlatılır; kullanıcı gerekirse "tekrar gönder" ile ister.
  const autoSent = useRef(false);
  useEffect(() => {
    if (!stateEmail || autoSent.current) return;
    autoSent.current = true;
    if (codeAlreadySent) {
      setResendIn(60);
      return;
    }
    void api.post('/auth/email-verification/resend', { email: stateEmail }).catch(() => {
      /* sessiz — enumeration-safe; kullanıcı "tekrar gönder" ile dener */
    });
    setResendIn(60);
  }, [stateEmail, codeAlreadySent]);

  async function onResend() {
    if (!email) {
      toast.error(t('verifyEmail.toast.emailRequired'));
      return;
    }
    setResending(true);
    try {
      await api.post('/auth/email-verification/resend', { email: email.trim().toLowerCase() });
    } catch {
      /* enumeration-safe — başarısız olsa da generic davran */
    } finally {
      toast.success(t('verifyEmail.toast.resent'));
      setResendIn(60);
      setResending(false);
    }
  }

  async function onSubmit(value: string) {
    if (!/^\d{6}$/.test(value)) {
      toast.error(t('verifyEmail.toast.codeLength'));
      return;
    }
    if (!email) {
      toast.error(t('verifyEmail.toast.emailRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/verify-email', { email: email.trim().toLowerCase(), code: value });
      toast.success(t('verifyEmail.toast.verified'));
      navigate('/giris');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('verifyEmail.toast.codeInvalid'));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('verifyEmail.title')}</h1>
        <p className="mb-6 text-sm text-custel-text-secondary">
          {stateEmail ? (
            <>
              <span className="font-medium text-custel-text">{stateEmail}</span> {t('verifyEmail.descriptionWithEmail')}
            </>
          ) : (
            t('verifyEmail.descriptionNoEmail')
          )}
        </p>

        <div className="space-y-4">
          {!stateEmail && (
            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="email">
                {t('verifyEmail.field.email')}
              </label>
              <input
                id="email"
                type="email"
                className="input-base"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-custel-text">{t('verifyEmail.field.code')}</label>
            <CustelOTPInput value={code} onChange={setCode} onComplete={onSubmit} disabled={submitting} autoFocus />
          </div>

          <button
            type="button"
            onClick={() => onSubmit(code)}
            className="btn-primary w-full"
            disabled={submitting || code.length !== 6}
          >
            {submitting ? t('verifyEmail.submitting') : t('verifyEmail.submit')}
          </button>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={onResend}
            disabled={resending || resendIn > 0}
            className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline disabled:cursor-not-allowed disabled:text-custel-text-tertiary disabled:no-underline"
          >
            {resendIn > 0 ? t('verifyEmail.resendCountdown', { seconds: resendIn }) : t('verifyEmail.resend')}
          </button>
          <Link to="/giris" className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline">
            {t('verifyEmail.backToLogin')}
          </Link>
        </div>
      </div>
    </main>
  );
}
